import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Building, CreditCard, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffBankDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    account_type: 'Savings'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/my-bank-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const bank = data.bank_details || {};
        setFormData({
          account_holder_name: bank.account_holder_name || '',
          account_number: bank.account_number || '',
          confirm_account_number: bank.account_number || '',
          bank_name: bank.bank_name || '',
          branch_name: bank.branch_name || '',
          ifsc_code: bank.ifsc_code || '',
          account_type: bank.account_type || 'Savings'
        });
      } else if (response.status === 401) {
        navigate('/staff-portal');
      }
    } catch (error) {
      console.error('Bank details fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = 'Account holder name is required';
    }
    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    }
    if (!formData.confirm_account_number.trim()) {
      newErrors.confirm_account_number = 'Please confirm account number';
    }
    if (formData.account_number !== formData.confirm_account_number) {
      newErrors.confirm_account_number = 'Account numbers do not match';
    }
    if (!formData.bank_name.trim()) {
      newErrors.bank_name = 'Bank name is required';
    }
    if (!formData.ifsc_code.trim()) {
      newErrors.ifsc_code = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code.toUpperCase())) {
      newErrors.ifsc_code = 'Invalid IFSC code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('staffToken');
      const formDataToSend = new FormData();
      formDataToSend.append('account_holder_name', formData.account_holder_name);
      formDataToSend.append('account_number', formData.account_number);
      formDataToSend.append('confirm_account_number', formData.confirm_account_number);
      formDataToSend.append('bank_name', formData.bank_name);
      formDataToSend.append('branch_name', formData.branch_name || '');
      formDataToSend.append('ifsc_code', formData.ifsc_code.toUpperCase());
      formDataToSend.append('account_type', formData.account_type);

      const response = await fetch(`${API_URL}/api/staff-portal/update-bank-details`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Bank details updated successfully');
      } else {
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/documents" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Bank Details</h1>
              <p className="text-indigo-200 text-sm">Update your bank account information</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Bank Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Account Holder Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.account_holder_name ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="As per bank records"
                />
                {errors.account_holder_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.account_holder_name}</p>
                )}
              </div>

              {/* Account Number */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.account_number ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter account number"
                  />
                  {errors.account_number && (
                    <p className="text-red-500 text-xs mt-1">{errors.account_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="confirm_account_number"
                    value={formData.confirm_account_number}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.confirm_account_number ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Re-enter account number"
                  />
                  {errors.confirm_account_number && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirm_account_number}</p>
                  )}
                </div>
              </div>

              {/* Bank Name & Branch */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.bank_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g., State Bank of India"
                  />
                  {errors.bank_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.bank_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch Name
                  </label>
                  <input
                    type="text"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Park Street Branch"
                  />
                </div>
              </div>

              {/* IFSC & Account Type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg uppercase ${errors.ifsc_code ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g., SBIN0001234"
                    maxLength={11}
                  />
                  {errors.ifsc_code && (
                    <p className="text-red-500 text-xs mt-1">{errors.ifsc_code}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Type
                  </label>
                  <select
                    name="account_type"
                    value={formData.account_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Savings">Savings Account</option>
                    <option value="Current">Current Account</option>
                  </select>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Please ensure your bank details are correct. 
                  Salary will be credited to this account. Don't forget to upload your 
                  Bank Passbook/Cheque in the Documents section.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/staff-portal/documents')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Bank Details
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
}
